import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Platform,
  TouchableOpacity,
  Linking,
  ActivityIndicator,
} from 'react-native';
import TopGreenHeader from '../Common/TopGreenHeader';
import { useNavigation } from '@react-navigation/native';
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import API_PATHS from '../../apiCalls/ApiPath';

const Events = () => {
  const navigation = useNavigation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    loadUserId();
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.GET_USER_EVENTS}`, {
        userId,
        limit: 10,
        page
      });

      if (response.data.Success === 1) {
        const newEvents = response.data.Data;
        if (page === 1) {
          setEvents(newEvents);
        } else {
          setEvents(prevEvents => [...prevEvents, ...newEvents]);
        }
        setHasMore(newEvents.length === 10);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, page]);

  useEffect(() => {
    if (userId) {
      fetchEvents();
    }
  }, [userId, page, fetchEvents]);

  const loadUserId = async () => {
    try {
      const storedUserId = await AsyncStorage.getItem('userId');
      if (storedUserId) {
        setUserId(storedUserId);
      }
    } catch (error) {
      console.error('Error loading userId:', error);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setPage(prevPage => prevPage + 1);
    }
  };

  const formatDate = (epochTime) => {
    const date = new Date(epochTime);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatTime = (time) => {
    return time.replace(/(\d+):(\d+)/, (match, hour, minute) => {
      const h = parseInt(hour);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${minute} ${ampm}`;
    });
  };

  const formatEventDates = (eventDates) => {
    return eventDates.map(date => {
      const formattedDate = formatDate(date.date);
      const startTime = date.startTime;
      const endTime = date.endTime;
      return `${formattedDate} ${startTime}-${endTime}`;
    }).join(', ');
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'ongoing':
        return styles.onGoing;
      case 'upcoming':
        return styles.upcoming;
      case 'completed':
        return styles.completed;
      default:
        return {};
    }
  };

  const handleEventPress = async (event) => {
    try {
      const storedUserId = await AsyncStorage.getItem('userId');
      
      if (Platform.OS === 'web') {
        window.open(`/event-details?eventId=${event.eventId}&userId=${storedUserId}`, '_blank');
      } else {
        navigation.navigate('EventDetails', { 
          event,
          userId: storedUserId 
        });
      }
    } catch (error) {
      console.error('Error getting userId:', error);
    }
  };

  const renderEvent = (event, index) => {
    const getTextStyle = (status) => {
      switch (status) {
        case 'ongoing':
          return styles.onGoingText;
        case 'upcoming':
          return styles.upcomingText;
        case 'completed':
          return styles.completedText;
        default:
          return {};
      }
    };

    const getRegistrationStatus = (event) => {
      if (event.status === 'completed') {
        return 'Registration Closed';
      }
      
      if (event.isRegistered) {
        return (
          <>
            Registration Opened <Text style={styles.bookedText}>(Booked)</Text>
          </>
        );
      }
      
      return `Registration Opened- ${event.available_seat_num}/${event.total_seats_num} remaining`;
    };

    return (
      <TouchableOpacity 
        key={index} 
        style={styles.eventCard}
        onPress={() => handleEventPress(event)}
        activeOpacity={0.7}
      >
        <View style={styles.eventContent}>
          <Text style={styles.eventTitle}>{event?.title}</Text>
          <Text style={styles.eventDate}>
            {formatEventDates(event?.eventDates)}
          </Text>
          <Text style={styles.registrationStatus}>
            {getRegistrationStatus(event)}
          </Text>
        </View>
        <View style={styles.statusContainer}>
          <View style={[styles.statusBadge, getStatusStyle(event?.status)]}>
            <Text style={[styles.statusText, getTextStyle(event?.status)]}>{event?.status}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderFooter = () => {
    if (!loadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color="#E94D8B" />
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <TopGreenHeader Heading="Events" navigation={navigation} isBackIcon={true} isNotification={true} />
      <ScrollView 
        style={styles.scrollView}
        onScroll={({ nativeEvent }) => {
          const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
          const paddingToBottom = 20;
          const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - paddingToBottom;
          
          if (isCloseToBottom && !loadingMore && hasMore) {
            handleLoadMore();
          }
        }}
        scrollEventThrottle={400}
      >
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#E94D8B" />
          </View>
        ) : (
          <>
            {events.map((event, index) => renderEvent(event, index))}
            {renderFooter()}
          </>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  scrollView: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  eventCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
    backgroundColor: '#ffffff',
  },
  eventContent: {
    flex: 1,
  },
  eventTitle: {
    fontSize: 18,
    color: '#000000',
    marginBottom: 5,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  eventDate: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 2,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  registrationStatus: {
    fontSize: 16,
    color: '#666666',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  bookedText: {
    color: '#EE3897',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans Bold',
  },
  statusContainer: {
    justifyContent: 'center',
    marginLeft: 10,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans Bold',
  },
  onGoing: {
    backgroundColor: '#B7410E66',
  },
  upcoming: {
    backgroundColor: '#00BFFF66',
  },
  completed: {
    backgroundColor: '#556B2F66',
  },
  statusText: {
    fontSize: 14,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  onGoingText: {
    color: '#B7410E',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
  },
  upcomingText: {
    color: '#00BFFF',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans ',
  },
  completedText: {
    color: '#556B2F',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans ',
  },
  bottomNav: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
    backgroundColor: '#ffffff',
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
  },
  navItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 15,
  },
  activeNavItem: {
    borderTopWidth: 2,
    borderTopColor: '#E94D8B',
  },
  navText: {
    fontSize: 12,
    color: '#666666',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
  },
  activeNavText: {
    color: '#E94D8B',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
});

export default Events; 